import { creatorbase } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import { UserOption, useUsersMultiSelect } from 'Modules/Plans/User/hooks/useUsersSelect';
import { FormikErrors, useFormikContext } from 'formik';
import { omit } from 'lodash';
import { useState } from 'react';
import { StylesConfig, ValueType } from 'react-select';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { FormField, SelectField, MoneyField } from '@round/whitelabel-ui';
import { Skeleton } from '@round/ui-kit';
import { useConditionalCurrencies } from 'contexts/OptionsContext/useConditionalCurrencies';
import { Tooltip } from '@round/whitelabel-ui';
import { Breakdown, InfoTooltip, Banner, assets } from '@round/whitelabel-ui';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import { DistributiveOmit } from 'utility/utility.types';
import * as yup from 'yup';
import styles from './CampaignFormBase.module.css';
import { ExternalMultiValuesControl } from '@round/whitelabel-ui';
import { None } from '@round/whitelabel-ui';
import { StaticTextValue } from '@round/whitelabel-ui';
import { RadioOptionCard } from '@round/whitelabel-ui';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import useProjectDetails from '../../useProjectDetails';
import { useClient, usePreviewPostCost, usePostPaymentRequest } from '@round/creatorbase-helpers/api-hooks';
import PaymentStatusBadge from 'Modules/Plans/Finance/components/PaymentStatusBadge/PaymentStatusBadge';
import { isCampaignCostLockedByPayment } from 'Modules/Plans/Campaigns/helpers';
import { useCurrentCampaignPaymentDetails } from '../ProjectCampaignsTable/CampaignPaymentRequestModal/useCurrentCampaignPaymentDetails';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import cn from 'classnames';

const { Warning: WarningIcon } = assets;

export type BaseCampaignFormValues = Pick<creatorbase.PostCampaignBody, 'name'> & {
    cost: string;
    is_post_cost_editable: boolean | null;
    currency: ValueType<GenericDropdownOption<number>, false>;
    team_members: ValueType<UserOption, true>;
};

export const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    is_post_cost_editable: yup.boolean().nullable().required('Spending type is required'),
    cost: yup.string().when('is_post_cost_editable', {
        is: (isPostCostEditable: boolean | null) => isPostCostEditable === false,
        then: yup
            .string()
            .required('Cost is required')
            .matches(/^[0-9.]+$/, {
                message: 'Cost should be a number',
                excludeEmptyString: true,
            }),
        otherwise: undefined,
    }),
    currency: yup.object().nullable().required('Currency is required'),
});

export const sharedCampaignValidationSchema = yup.object({
    name: yup.string().required('Name is required'),
});

export const mapCampaignApiErrorsToFormikErrors = (
    apiErrors: Pick<
        creatorbase.PostCampaignErrorResponse,
        'cost' | 'currency_id' | 'is_post_cost_editable' | 'name' | 'team_members'
    >
): FormikErrors<BaseCampaignFormValues> =>
    mapApiErrorsToFormikErrors({
        cost: apiErrors.cost,
        currency: apiErrors.currency_id,
        is_post_cost_editable: apiErrors.is_post_cost_editable,
        name: apiErrors.name,
        team_members: apiErrors.team_members,
    });

type ApiParameters = DistributiveOmit<creatorbase.PostCampaignBody, 'project_id'>;
export const mapCampaignFormValuesToApiData = (
    values: BaseCampaignFormValues,
    arePaymentsEnabled = false
): DistributiveOmit<ApiParameters, 'platform'> => {
    const base = {
        currency_id: values.currency?.value!,
        name: values.name,
        team_members: values.team_members?.map((user) => user.value) ?? [],
        is_post_cost_editable: values.is_post_cost_editable!,
    };

    if (values.is_post_cost_editable) {
        return { ...base, is_post_cost_editable: true, cost: null };
    }

    if (arePaymentsEnabled) {
        return { ...base, is_post_cost_editable: false, base_cost: Number(values.cost) };
    }

    return { ...base, is_post_cost_editable: false, cost: Number(values.cost) };
};

export const getBaseCampaignFormFields = (fields?: string[]) => {
    const baseFields: Array<keyof BaseCampaignFormValues> = [
        'cost',
        'currency',
        'is_post_cost_editable',
        'name',
        'team_members',
    ];

    return fields?.filter((f): f is keyof BaseCampaignFormValues =>
        baseFields.includes(f as keyof BaseCampaignFormValues)
    );
};

const currencySelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        pointerEvents: 'initial',
    }),
};

type Props = {
    teamId: number | undefined;
    campaignId?: number;
    paymentRequestId?: number | null;
    readonlyFields?: Array<keyof BaseCampaignFormValues>;
    isManagedExternally?: boolean;
};

const CampaignFormBase = ({ readonlyFields, teamId, campaignId, paymentRequestId, isManagedExternally }: Props) => {
    const isCurrentUserCreatorbaseAdmin = useCheckUserGroupsAccess(['creatorbase_admin'], 'all');
    const creatorbaseCurrencies = useConditionalCurrencies(true);
    const { getCurrencyById } = useCurrencies();
    const isCampaignPaymentRequestsEnabled = useFeatureFlags().isEnabled('rou_539_campaign_payment_requests');

    const { team } = useProjectDetails();
    const { client } = useClient(team?.client_id);
    const isClientPaymentEnabled = !!client?.is_payment_enabled;

    const { values, initialValues, setFieldError, setFieldValue } = useFormikContext<BaseCampaignFormValues>();
    const [currencyValueAnchor, setCurrencyValueAnchor] = useState<HTMLSpanElement | null>(null);

    const { getProps: getTeamMembersProps } = useUsersMultiSelect({
        initOn: 'menuOpen',
        params: {
            team_id: teamId,
            ...(!isCurrentUserCreatorbaseAdmin && { is_creatorbase_admin: false }),
        },
    });

    const isRemovable = (option: UserOption) => {
        if (isCurrentUserCreatorbaseAdmin) {
            return true;
        }
        return !option.is_creatorbase_admin;
    };

    const setIsPostCostEditable = (isPostCostEditable: boolean) => {
        if (isPostCostEditable === values.is_post_cost_editable) {
            return;
        }

        setFieldError('is_post_cost_editable', undefined);
        setFieldError('currency', undefined);
        setFieldError('cost', undefined);

        setFieldValue('is_post_cost_editable', isPostCostEditable);
    };

    // Campaign total billing produces a single cost, so we can preview its fee-inclusive total the same
    // way the post form does. The breakdown is shown as soon as the client has payments enabled and the
    // budget type is campaign total. For an existing campaign we surface the stored total from its saved
    // payment details and only re-preview once the cost inputs are edited away from their initial values.
    const showCostWithFees =
        isCampaignPaymentRequestsEnabled && isClientPaymentEnabled && values.is_post_cost_editable === false;

    const numericCost = Number(values.cost);
    const hasCost = !!values.cost && !Number.isNaN(numericCost);

    const isExistingCampaign = typeof campaignId === 'number';
    const {
        campaignPaymentDetails,
        isLoading: arePaymentDetailsLoading,
        isFetching: arePaymentDetailsFetching,
        isSuccess: arePaymentDetailsSuccess,
        hasError: paymentDetailsError,
    } = useCurrentCampaignPaymentDetails(
        { campaignId: campaignId, paymentRequestId },
        { enabled: showCostWithFees && isExistingCampaign }
    );

    const hasCostChanged = Number(values.cost) !== Number(initialValues.cost);
    const hasCurrencyChanged = values.currency?.value !== initialValues.currency?.value;
    const haveCostInputsChanged = hasCostChanged || hasCurrencyChanged;

    // A new campaign never has saved details; an existing one only counts as having none once its fetch
    // resolves empty. Re-preview when there's nothing stored, the stored payment failed, or the cost
    // inputs were edited.
    const hasNoExistingPaymentDetails = !isExistingCampaign || (arePaymentDetailsSuccess && !campaignPaymentDetails);
    const shouldFetchPreview = showCostWithFees && hasCost && (hasNoExistingPaymentDetails || haveCostInputsChanged);

    const {
        data: costPreview,
        isLoading: isPreviewLoading,
        error: costPreviewError,
    } = usePreviewPostCost(
        {
            campaign_id: campaignId,
            // Without a campaign to resolve the client's fee config, the preview needs the client directly.
            client_id: campaignId ? undefined : team?.client_id,
            base_cost: hasCost ? numericCost : undefined,
            currency_id: values.currency?.value,
        },
        { enabled: shouldFetchPreview }
    );

    const { data: paymentRequest } = usePostPaymentRequest(paymentRequestId, {
        enabled: typeof paymentRequestId === 'number',
    });
    const showPaymentStatusBadge = isCampaignCostLockedByPayment(paymentRequest?.status);

    const costWithFees = shouldFetchPreview ? costPreview?.cost : campaignPaymentDetails?.cost;
    const isCostWithFeesLoading = isPreviewLoading || arePaymentDetailsLoading;
    const costWithFeesHasError = shouldFetchPreview ? costPreviewError : paymentDetailsError;

    const renderCostField = () => (
        <MoneyField
            name="cost"
            currencyFieldName="currency"
            label={values.is_post_cost_editable ? 'Campaign currency' : 'Campaign cost'}
            isCurrencyDisabled={readonlyFields?.includes('currency') || arePaymentDetailsFetching}
            disabled={readonlyFields?.includes('cost') || arePaymentDetailsFetching}
            hideCostInput={values.is_post_cost_editable ?? undefined}
            currencySelectStyles={currencySelectStyles}
            className={styles.moneyInput}
            formatOptionLabel={(option: GenericDropdownOption<number>, { context }: { context: 'menu' | 'value' }) => {
                if (context === 'value') {
                    return <span ref={setCurrencyValueAnchor}>{option.label}</span>;
                }

                return option.label;
            }}
            currencies={creatorbaseCurrencies}
        />
    );

    return (
        <>
            <FormField
                name="name"
                label="Campaign name"
                placeholder="Enter a campaign name"
                disabled={readonlyFields?.includes('name')}
            />

            <FormField
                name="is_post_cost_editable"
                label="Budget type"
                className={styles.spendingTypeContainer}
                component={() => (
                    <div className={styles.spendingTypeHorizontal}>
                        <RadioOptionCard
                            name="is_post_cost_editable"
                            label="Per Post / Creator"
                            subLabel="Set individual costs for each creator post"
                            tooltip="Best when you're briefing creators and negotiating rates separately"
                            isDisabled={readonlyFields?.includes('is_post_cost_editable')}
                            onClick={() => setIsPostCostEditable(true)}
                            isSelected={values.is_post_cost_editable === true}
                            hideRadio
                        />

                        <RadioOptionCard
                            name="is_post_cost_editable"
                            label="Campaign Total"
                            subLabel="Set one total budget for the entire campaign"
                            tooltip="Best when you've agreed a single fee covering multiple posts or a third-party partner"
                            isDisabled={readonlyFields?.includes('is_post_cost_editable')}
                            onClick={() => setIsPostCostEditable(false)}
                            isSelected={values.is_post_cost_editable === false}
                            hideRadio
                        />
                    </div>
                )}
            />

            {typeof values.is_post_cost_editable === 'boolean' && (
                <>
                    {showCostWithFees ? (
                        <div className={styles.costSection}>
                            <Breakdown.Container className={styles.costContainer}>
                                <div className={styles.fieldRow}>
                                    <div className={styles.field}>
                                        {arePaymentDetailsLoading ? (
                                            <Skeleton height="3.5rem" borderRadius="0.5rem" />
                                        ) : (
                                            renderCostField()
                                        )}
                                    </div>

                                    {showPaymentStatusBadge && paymentRequest && (
                                        <PaymentStatusBadge status={paymentRequest.status} />
                                    )}
                                </div>

                                <Breakdown.Separator className={styles.separator} />

                                <Breakdown.Item isTotal>
                                    <Breakdown.Label>
                                        <span className={styles.row}>
                                            <span>Total Cost</span> <InfoTooltip>Includes estimated fees</InfoTooltip>
                                        </span>
                                    </Breakdown.Label>
                                    <Breakdown.Amount
                                        amount={hasCost ? costWithFees : undefined}
                                        currency={getCurrencyById(values.currency?.value)}
                                        className={cn({ [styles.errorAmount]: costWithFeesHasError })}
                                        isLoading={isCostWithFeesLoading}
                                    />
                                </Breakdown.Item>
                            </Breakdown.Container>

                            {costWithFeesHasError && (
                                <Banner appearance="destructive" className={styles.banner}>
                                    <WarningIcon className={styles.errorIcon} />
                                    <p>Something went wrong fetching estimated fees</p>
                                </Banner>
                            )}
                        </div>
                    ) : (
                        renderCostField()
                    )}

                    {readonlyFields?.includes('currency') && (
                        <Tooltip anchorElement={currencyValueAnchor} placement="top-start">
                            <Tooltip.Title>Unable to change campaign currency</Tooltip.Title>
                            <Tooltip.Body>
                                {isManagedExternally
                                    ? 'Campaign is managed externally'
                                    : 'You cannot change currency if you have 2 or more campaigns'}
                            </Tooltip.Body>
                        </Tooltip>
                    )}
                </>
            )}

            <SelectField
                name="team_members"
                label="Planners"
                placeholder="Select planners"
                isClearable={false}
                controlShouldRenderValue={false}
                {...omit(getTeamMembersProps(), ['value', 'onChange'])}
                components={{
                    Control: ExternalMultiValuesControl,
                    Input: StaticTextValue,
                    MultiValue: None,
                    Placeholder: None,
                }}
                customSelectProps={{
                    isRemovable: isRemovable,
                }}
            />
        </>
    );
};

export default CampaignFormBase;
